import { supabase } from '@/lib/supabase'
import { getApiBaseUrl } from '@/lib/api-url'

const API_BASE_URL = getApiBaseUrl()

export interface ApiResponse<T> {
  data: T
  error?: string
}

export interface PaginatedResponse<T> {
  data: T[]
  pagination: {
    page: number
    page_size: number
    total: number
    total_pages: number
  }
}

export class SuperAdminApiError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public details?: unknown
  ) {
    super(message)
    this.name = 'SuperAdminApiError'
  }
}

async function getAuthHeader(): Promise<string> {
  const {
    data: { session },
  } = await supabase.auth.getSession()

  if (!session?.access_token) {
    throw new SuperAdminApiError('Not authenticated', 401)
  }

  return `Bearer ${session.access_token}`
}

async function handleResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get('content-type') || ''

  if (!response.ok) {
    const errorData = contentType.includes('application/json')
      ? await response.json().catch(() => ({}))
      : {}
    throw new SuperAdminApiError(
      errorData.message || `Request failed with status ${response.status}`,
      response.status,
      errorData
    )
  }

  if (!contentType.includes('application/json')) {
    throw new SuperAdminApiError(
      'A API respondeu um conteúdo inválido. Verifique se o backend local está rodando na porta correta.',
      response.status
    )
  }

  return response.json()
}

export const superAdminApi = {
  async get<T>(endpoint: string, params?: Record<string, string | number | boolean | undefined>): Promise<T> {
    const authHeader = await getAuthHeader()

    const url = new URL(`${API_BASE_URL}/api/super-admin${endpoint}`)

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value))
        }
      })
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
      },
    })

    return handleResponse<T>(response)
  },

  async post<T, D = unknown>(endpoint: string, data?: D): Promise<T> {
    const authHeader = await getAuthHeader()

    const response = await fetch(`${API_BASE_URL}/api/super-admin${endpoint}`, {
      method: 'POST',
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data ?? {}),
    })

    return handleResponse<T>(response)
  },

  async patch<T, D = unknown>(endpoint: string, data?: D): Promise<T> {
    const authHeader = await getAuthHeader()

    const response = await fetch(`${API_BASE_URL}/api/super-admin${endpoint}`, {
      method: 'PATCH',
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data ?? {}),
    })

    return handleResponse<T>(response)
  },

  async delete<T, D = unknown>(endpoint: string, data?: D): Promise<T> {
    const authHeader = await getAuthHeader()

    const response = await fetch(`${API_BASE_URL}/api/super-admin${endpoint}`, {
      method: 'DELETE',
      headers: {
        Authorization: authHeader,
        'Content-Type': 'application/json',
      },
      body: data === undefined ? undefined : JSON.stringify(data),
    })

    return handleResponse<T>(response)
  },
}
